import type React from "react";

import { useTranslation } from "react-i18next";

import Color from "color";
import { useSessionStore } from "stores/session";

import { css, cx, styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp, useTheme } from "simplydo/react/theme";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { H1 } from "simplydo/react/ui/header";

import { useCurrentUser } from "utils/currentUser";

export const StyledDashboardContent = styled.div`
  flex: 1;
  position: relative;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
`;

const transitionSegmentClass = css`
  opacity: 0;
  animation-duration: 0.7s;
  animation-fill-mode: forwards;
  @keyframes slideInFull {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: 0;
      opacity: 1;
    }
  }
  @keyframes slideInOffset {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: -5%;
      opacity: 1;
    }
  }
  &.slide-full {
    animation-name: slideInFull;
  }
  &.slide-offset {
    animation-name: slideInOffset;
  }
`;

const MaxContent = styled.div`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;

  min-height: 40px;

  &.is-scrolling {
    max-height: 70vh;
  }

  ${mobileOnly} {
    padding-right: 0px;
  }

  ${tabletUp} {
    padding-right: 10px;
  }
`;

// Better visual gradient, source https://css-tricks.com/easing-linear-gradients/
const scrimGradient = (colorStr: string) => {
  const color = Color(colorStr);

  return `radial-gradient(
    80vw 80vh at 100% 100%,
    ${color.rgb()} 0%,
    ${color.alpha(0.738).rgb()} 19%,
    ${color.alpha(0.541).rgb()} 34%,
    ${color.alpha(0.382).rgb()} 47%,
    ${color.alpha(0.278).rgb()} 56.5%,
    ${color.alpha(0.194).rgb()} 65%,
    ${color.alpha(0.126).rgb()} 73%,
    ${color.alpha(0.075).rgb()} 80.2%,
    ${color.alpha(0.042).rgb()} 86.1%,
    ${color.alpha(0.021).rgb()} 91%,
    ${color.alpha(0.008).rgb()} 95.2%,
    ${color.alpha(0.002).rgb()} 98.2%,
    transparent 100%
  )`;
};

type StyledDashboardProps = {
  content: React.ReactNode;
  hideName?: boolean;
  fluid?: boolean;
  excludeWelcomeText?: boolean;
  scrolling?: boolean;
  backgroundColor?: string;
};

const AppStyledDashboard = ({
  content,
  hideName,
  fluid,
  excludeWelcomeText,
  scrolling = true,
  backgroundColor,
}: StyledDashboardProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const urlOrganisation = useSessionStore((state) => state.urlOrganisation);
  const organisation = user?.ownerOrganisation || urlOrganisation;
  const theme = useTheme();

  return (
    <StyledDashboardContent
      style={{
        paddingTop: scrolling ? 0 : 80,
        paddingBottom: scrolling ? 0 : 80,
      }}
    >
      <div
        style={{
          transition: "opacity 0.3s ease",
          opacity: organisation ? 1 : 0,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: scrimGradient(organisation?.primaryColour || "#fff"),
        }}
      />
      {backgroundColor ? (
        <div
          style={{
            transition: "opacity 0.3s ease",
            opacity: organisation ? 0 : 1,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: scrimGradient(backgroundColor),
          }}
        />
      ) : null}
      <Container
        fluid
        style={{ alignItems: "center", justifyContent: "center" }}
      >
        <div
          style={{
            width: theme.sizes.isMobile ? "100%" : fluid ? "62.5%" : "37.5%",
            minWidth: theme.sizes.isMobile ? undefined : 600,
            maxWidth: "100%",
          }}
        >
          <Card
            elevation="large"
            className={cx(
              transitionSegmentClass,
              theme.sizes.isMobile || fluid ? "slide-full" : "slide-offset",
            )}
            fluid
          >
            <CardContent style={{ padding: "2rem" }}>
              {!excludeWelcomeText ? (
                <>
                  <H1>
                    <span
                      role="img"
                      aria-label="Wave"
                      style={{ marginRight: 12 }}
                    >
                      👋
                    </span>
                    {!hideName && user?.profile?.firstName
                      ? t("entry.enrol.welcome_name", {
                          firstName: user?.profile?.firstName,
                        })
                      : t("entry.enrol.welcome")}
                  </H1>
                  <Divider />
                </>
              ) : null}
              <MaxContent className={cx(scrolling && "is-scrolling")}>
                {content}
              </MaxContent>
            </CardContent>
          </Card>
        </div>
      </Container>
    </StyledDashboardContent>
  );
};

export default AppStyledDashboard;
